import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { Button, Modal } from "antd";
import { useTranslation } from "react-i18next";
import { useUser } from "../../../../contexts/UserContext/UserContext";
import { useToast } from "../../../../contexts/ToastContext";
import { PaymentMethodSelector, buildAdminPaymentOptions, getDefaultPaymentMethod } from "../../lib/reservationPayment";
import confirmDuePaymentFlow from "../../lib/confirmDuePaymentFlow";
import useSolaCardPayment from "../../../../components/payment/useSolaCardPayment";

const PaymentOptionModal = ({ open, onClose, resDetails, fetchResDetails }) => {
    const { myCompanyData: companyData } = useUser();
    const toast = useToast();
    const { t } = useTranslation(["reservations", "checkIn", "reservationPayment"]);
    const { requestSolaCardPayment, solaCardModal } = useSolaCardPayment();
    const [paymentSystem, setPaymentSystem] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const defaultPaymentGateway = companyData?.defaultPaymentGateway || "stripe";
    const showCashPayment = companyData?.preferences?.showCashPayment === true;

    const paymentOptions = useMemo(
        () =>
            buildAdminPaymentOptions({
                defaultPaymentGateway,
                showCashPayment,
                t,
            }),
        [defaultPaymentGateway, showCashPayment, t],
    );

    const dueAmount = Number(resDetails?.invoice?.due ?? 0);

    useEffect(() => {
        if (!open) {
            setPaymentSystem(null);
            setIsSubmitting(false);
            return;
        }
        if (paymentOptions.length > 0) {
            setPaymentSystem(getDefaultPaymentMethod(paymentOptions));
        }
    }, [open, paymentOptions]);

    const handleConfirm = async () => {
        if (!resDetails || isSubmitting) return;

        if (!paymentSystem) {
            toast.error({ message: t("checkIn:selectPaymentRequired") });
            return;
        }

        setIsSubmitting(true);
        try {
            const result = await confirmDuePaymentFlow({
                reservation: resDetails,
                dueAmount,
                paymentSystem,
                companyData,
                toast,
                t,
                requestSolaCardPayment,
            });

            if (result?.type === "redirect" || result?.type === "cancelled") {
                return;
            }

            if (typeof fetchResDetails === "function") {
                await fetchResDetails();
            }

            toast.success({ message: t("payment.paymentSuccessful") });
            onClose();
        } catch (error) {
            toast.error({
                message: t("payment.failedToMakePayment"),
                description: error?.response?.data?.message || error?.message,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Modal
                title={t("payment.confirmPayment")}
                open={open}
                onCancel={onClose}
                centered
                width="min(560px, calc(100vw - 32px))"
                destroyOnClose
                maskClosable={false}
                footer={
                    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                        <Button onClick={onClose} disabled={isSubmitting} className="!rounded-lg">
                            {t("no")}
                        </Button>
                        <Button type="primary" loading={isSubmitting} onClick={handleConfirm} className="!rounded-lg">
                            {t("payment.confirmPayment")}
                        </Button>
                    </div>
                }>
                <div className="min-w-0">
                    {dueAmount > 0 ? (
                        <p className="fontRegular mb-4 text-sm text-[#667085]">
                            {t("checkIn:paymentSubtitle")}{" "}
                            <span className="fontSemi text-[#2F2F46]">${dueAmount.toFixed(2)}</span>
                        </p>
                    ) : null}
                    <PaymentMethodSelector
                        compact
                        title={t("checkIn:paymentTitle")}
                        options={paymentOptions}
                        value={paymentSystem}
                        onChange={setPaymentSystem}
                    />
                </div>
            </Modal>
            {solaCardModal}
        </>
    );
};

PaymentOptionModal.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    resDetails: PropTypes.object,
    fetchResDetails: PropTypes.func,
};

export default PaymentOptionModal;
